import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Scale, Shield, FileText, Users, Eye, Lock, AlertTriangle } from 'lucide-react';

const policies = [
  {
    icon: Scale,
    title: 'Conflict of Interest Policy',
    content: 'All IMCAC officers, examiners, and board members must disclose any financial, professional, or personal relationships that could influence decision-making. Annual declarations are required, and conflicted individuals must recuse themselves from relevant decisions. Violations result in removal from the role.'
  },
  {
    icon: AlertTriangle,
    title: 'Appeal Mechanism',
    content: 'Candidates may submit a formal appeal within 14 days of receiving examination results. Appeals are reviewed by an independent committee separate from the examination process. Decisions are communicated within 30 days. Personal hearings may be granted where circumstances warrant additional review.'
  },
  {
    icon: Lock,
    title: 'Data Protection Policy',
    content: 'IMCAC follows GDPR and local data privacy legislation. All personal data is collected and processed securely with encryption. Candidates have the right to access, correct, and request deletion of their data. Staff handling personal data undergo regular compliance training.'
  },
  {
    icon: Users,
    title: 'Independent Academic Board',
    content: 'The IMCAC Academic Board operates independently from training providers and commercial interests. Board members are appointed through a transparent process, serve fixed terms, and must meet strict criteria for academic qualifications and clinical expertise.'
  },
  {
    icon: Shield,
    title: 'Examiner Training Program',
    content: 'All examiners undergo a rigorous initial training period covering examination standards, candidate interaction, and score calibration. Regular calibration sessions maintain consistency across examiners. Performance is audited annually with feedback and remediation as needed.'
  },
  {
    icon: Eye,
    title: 'Separation of Training & Examination',
    content: 'IMCAC maintains strict separation between training providers and the examination authority. No individual or institution involved in delivering IMCAC-accredited training programs may participate in the assessment or certification of their own graduates.'
  },
];

export default function EthicsPage() {
  return (
    <div data-testid="ethics-page">
      <section className="bg-[#1B2A4A] py-16 md:py-20">
        <div className="imcac-container">
          <p className="imcac-overline mb-3">Institutional Integrity</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight font-heading">Ethics & Governance</h1>
          <div className="h-1 w-20 bg-[#C5A572] mt-5" />
          <p className="text-lg text-slate-300 mt-4 max-w-2xl">
            IMCAC upholds the highest standards of governance, transparency, and ethical practice in all operations.
          </p>
        </div>
      </section>

      <section className="imcac-section bg-white">
        <div className="imcac-container">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {policies.map((policy, i) => (
              <Card key={i} className="imcac-card" data-testid={`ethics-policy-${i}`}>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-[#1B2A4A] rounded-sm flex items-center justify-center">
                      <policy.icon className="h-5 w-5 text-[#C5A572]" />
                    </div>
                    <h3 className="text-lg font-semibold text-[#1B2A4A] font-heading">{policy.title}</h3>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed">{policy.content}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="imcac-section bg-[#F8FAFC]">
        <div className="imcac-container max-w-3xl text-center">
          <h2 className="text-3xl font-semibold text-[#1B2A4A] font-heading mb-4">Our Commitment</h2>
          <p className="text-base text-slate-600 leading-relaxed">
            IMCAC is committed to maintaining public trust through rigorous governance frameworks, 
            transparent processes, and unwavering adherence to ethical principles. Our governance structure 
            ensures accountability, fairness, and independence in all certification activities.
          </p>
        </div>
      </section>
    </div>
  );
}
